import React, { useState, useEffect, useRef } from 'react';
import { Shield, RefreshCw, X, CheckCircle } from 'react-feather';

interface VerificationCodePromptProps {
  channel: 'sms' | 'email';
  maskedDestination: string;
  canResend?: boolean;
  error?: string | null;
  disabled?: boolean;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onSkip?: () => void;
  skipLabel?: string;
}

const RESEND_COOLDOWN_SECONDS = 30;

const VerificationCodePrompt: React.FC<VerificationCodePromptProps> = ({
  channel,
  maskedDestination,
  canResend = true,
  error,
  disabled = false,
  onSubmit,
  onResend,
  onSkip,
  skipLabel = 'Skip — continue as guest',
}) => {
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = code.trim();
    if (cleaned.length !== 6) return;
    onSubmit(cleaned);
  };

  const handleResend = () => {
    if (!canResend || cooldown > 0) return;
    setCode('');
    setCooldown(RESEND_COOLDOWN_SECONDS);
    onResend();
  };

  const channelLabel = channel === 'sms' ? 'text message' : 'email';

  return (
    <form
      onSubmit={handleSubmit}
      className="service-request-card service-request-card--customer"
    >
      <div className="service-request-card__header">
        <div
          className="service-request-card__icon service-request-card__icon--customer"
          style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
        >
          <Shield size={20} />
        </div>
        <h3 className="service-request-card__title">Verify to Link Your Account</h3>
      </div>

      <div className="service-request-card__content" style={{ gap: 10 }}>
        <p className="service-request-card__description" style={{ marginBottom: 4 }}>
          We sent a 6-digit code to your {channelLabel} at{' '}
          <strong>{maskedDestination}</strong>. Enter it below to link any equipment
          we already have on file.
        </p>

        <input
          ref={inputRef}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          disabled={disabled}
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: 10,
            border: '1.5px solid #e5e7eb',
            fontSize: '1.4rem',
            letterSpacing: '0.4rem',
            textAlign: 'center',
            fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
            outline: 'none',
          }}
        />

        {error && (
          <div style={{ color: '#dc2626', fontSize: '0.82rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={handleResend}
            disabled={disabled || !canResend || cooldown > 0}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 10px',
              background: 'transparent',
              border: 'none',
              color: cooldown > 0 || !canResend ? '#9ca3af' : '#4f46e5',
              cursor: cooldown > 0 || !canResend ? 'not-allowed' : 'pointer',
              fontSize: '0.82rem',
              fontWeight: 600,
            }}
          >
            <RefreshCw size={13} />
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </button>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              disabled={disabled}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 10px',
                background: 'transparent',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: 600,
              }}
            >
              <X size={13} />
              {skipLabel}
            </button>
          )}
        </div>
      </div>

      <div className="service-request-card__actions">
        <button
          type="submit"
          disabled={disabled || code.length !== 6}
          style={{
            width: '100%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '10px 14px',
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: disabled || code.length !== 6 ? 'not-allowed' : 'pointer',
            opacity: disabled || code.length !== 6 ? 0.5 : 1,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
        >
          <CheckCircle size={16} />
          Verify & Link Account
        </button>
      </div>
    </form>
  );
};

export default VerificationCodePrompt;
